// Remote half of saved-trip sync: the `saved_routes` table.
//
// This file only talks to Supabase. It knows the SavedTrip shape but holds no
// local state and makes no merge decisions -- that is savedTrips.ts's job.
//
// Every function here fails soft. A saved trip that cannot reach the server is
// still a saved trip on this device; sync retries on the next opportunity. The
// one thing we never do is throw into a UI path and lose the user's save.

import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import type { SavedTrip, SavedTripSettings } from "@/utils/savedTrips";
import type { TravelMode } from "@/api/routing";

/** A trip as it comes back from the server, plus sync bookkeeping. */
export interface RemoteTrip {
  trip: SavedTrip;
  /** Server-stamped. Authoritative for last-writer-wins -- never a device clock. */
  updatedAt: number;
  deleted: boolean;
}

const MODES: TravelMode[] = ["driving", "walking", "biking"];

function safeMode(m: unknown): TravelMode {
  return MODES.includes(m as TravelMode) ? (m as TravelMode) : "driving";
}

/** `name` is NOT NULL with a 1..80 check constraint -- never send it empty. */
function safeName(label: string): string {
  const t = (label ?? "").trim();
  return (t.length ? t : "Saved trip").slice(0, 80);
}

/**
 * Map a local SavedTrip onto the saved_routes columns.
 * Coordinates go in real columns so the row stays queryable; everything the
 * table has no column for rides in `payload`.
 */
function toRow(userId: string, t: SavedTrip) {
  return {
    user_id: userId,
    client_id: t.id,
    name: safeName(t.label),
    label: t.label.slice(0, 40),
    travel_mode: safeMode(t.settings?.mode),
    start_lat: t.start.coords[1],
    start_lng: t.start.coords[0],
    end_lat: t.end.coords[1],
    end_lng: t.end.coords[0],
    start_label: t.start.place ?? null,
    end_label: t.end.place ?? null,
    // Cast to Json: the generated column type is Json, and our interfaces are
    // structurally JSON-safe but lack the index signature Json requires. The
    // shape is asserted by the round-trip through fromRow(), which is what the
    // sync tests exercise.
    payload: {
      v: 1,
      start: t.start,
      end: t.end,
      stops: t.stops ?? [],
      settings: t.settings ?? null,
      createdAt: t.createdAt,
    } as unknown as Json,
    deleted_at: null,
  };
}

/** Rebuild a SavedTrip from a row, preferring `payload` and falling back to columns. */
function fromRow(row: Record<string, unknown>): RemoteTrip | null {
  const clientId = row.client_id as string | null;
  if (!clientId) return null; // legacy saveRoute() row -- not a synced trip

  const payload = (row.payload ?? {}) as {
    start?: SavedTrip["start"];
    end?: SavedTrip["end"];
    stops?: SavedTrip["stops"];
    settings?: SavedTripSettings | null;
    createdAt?: number;
  };

  const start =
    payload.start ??
    {
      name: (row.start_label as string) ?? "Start",
      place: (row.start_label as string) ?? "",
      coords: [row.start_lng as number, row.start_lat as number] as [number, number],
    };
  const end =
    payload.end ??
    {
      name: (row.end_label as string) ?? "Destination",
      place: (row.end_label as string) ?? "",
      coords: [row.end_lng as number, row.end_lat as number] as [number, number],
    };

  if (!Array.isArray(start.coords) || !Array.isArray(end.coords)) return null;

  return {
    trip: {
      id: clientId,
      label: ((row.label as string) ?? (row.name as string) ?? "Saved trip").slice(0, 40),
      start,
      end,
      stops: payload.stops ?? [],
      settings: payload.settings ?? undefined,
      createdAt: payload.createdAt ?? (Date.parse((row.created_at as string) ?? "") || Date.now()),
      updatedAt: Date.parse((row.updated_at as string) ?? "") || Date.now(),
    },
    updatedAt: Date.parse((row.updated_at as string) ?? "") || 0,
    deleted: row.deleted_at != null,
  };
}

/** Every synced trip for this user, tombstones included (the merge needs them). */
export async function pullTrips(userId: string): Promise<RemoteTrip[] | null> {
  const { data, error } = await supabase
    .from("saved_routes")
    .select("*")
    .eq("user_id", userId)
    .not("client_id", "is", null)
    .order("updated_at", { ascending: false })
    .limit(200);

  if (error) {
    console.warn("[savedTripsSync] pull failed:", error.message);
    return null;
  }
  return (data ?? [])
    .map((r) => fromRow(r as Record<string, unknown>))
    .filter((r): r is RemoteTrip => r !== null);
}

/** Upsert one trip. Returns false when the write did not land. */
export async function pushTrip(userId: string, t: SavedTrip): Promise<boolean> {
  const { error } = await supabase
    .from("saved_routes")
    .upsert(toRow(userId, t), { onConflict: "user_id,client_id" });

  if (error) {
    console.warn("[savedTripsSync] push failed:", error.message);
    return false;
  }
  return true;
}

/**
 * Tombstone a trip rather than deleting the row, so the delete survives a pull
 * on another device. If the row was never pushed there is nothing to mark and
 * the no-op is the correct outcome.
 */
export async function pushDelete(userId: string, clientId: string): Promise<boolean> {
  const { error } = await supabase
    .from("saved_routes")
    .update({ deleted_at: new Date().toISOString() })
    .eq("user_id", userId)
    .eq("client_id", clientId);

  if (error) {
    console.warn("[savedTripsSync] delete failed:", error.message);
    return false;
  }
  return true;
}
